import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react';
import { useAppStore } from '../../state/store';
import { useSeriesOptions } from '../../utils/useSeriesOptions';
import { InstrumentPill, ValuePill, FromIndicatorBadge } from './IndicatorPreviewFields';

export type IndicatorEditorSMARef = {
  getConfig: () => {
    instrumentId: string;
    field: string;
    period: number;
    lag: number;
  };
};

type Props = {
  config: {
    instrumentId: string;
    field?: string;
    period?: number;
    lag?: number;
  };
  onChange: (config: Props['config']) => void;
  editMode: boolean;
  indicatorId?: string; // passed when editing an existing indicator
};

const IndicatorEditorSMA = forwardRef<IndicatorEditorSMARef, Props>(
  ({ config, onChange, editMode, indicatorId }, ref) => {
    const [period, setPeriod] = useState(config.period ?? 20);
    const [lag, setLag] = useState(config.lag ?? 0);

    // Unified series options (instruments + indicators)
    const allSeriesOptions = useSeriesOptions(indicatorId);
    const hasSingleOption = allSeriesOptions.length === 1;

    // Auto-select the only option if applicable
    useEffect(() => {
      if (hasSingleOption && config.instrumentId !== allSeriesOptions[0].id) {
        onChange({ ...config, instrumentId: allSeriesOptions[0].id });
      }
    }, [hasSingleOption, allSeriesOptions, config.instrumentId, onChange]);

    // Find the currently selected series
    const selectedSeries = allSeriesOptions.find(s => s.id === config.instrumentId);

    // Determine available fields based on selected series
    const availableFields = selectedSeries
      ? Object.keys(selectedSeries.emittedFields).filter(
        field => selectedSeries.emittedFields[field] === 'Number'
      )
      : [];

    // Current field state – default to Close when available, else first Number field
    const currentField = config.field && availableFields.includes(config.field)
      ? config.field
      : availableFields.includes('Close')
        ? 'Close'
        : availableFields[0] || 'Value';

    // Sync field when series changes
    useEffect(() => {
      if (config.field !== currentField) {
        onChange({ ...config, field: currentField });
      }
    }, [currentField, config, onChange]);

    const handleSeriesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange({ ...config, instrumentId: e.target.value });
    };

    const handlePeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value);
      if (!isNaN(val) && val > 0) {
        setPeriod(val);
        onChange({ ...config, period: val });
      }
    };

    const handleLagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value);
      if (!isNaN(val) && val >= 0) {
        setLag(val);
        onChange({ ...config, lag: val });
      }
    };

    useImperativeHandle(ref, () => ({
      getConfig: () => ({
        instrumentId: config.instrumentId,
        field: currentField,
        period,
        lag,
      }),
    }));

    // ── Preview mode: discrete fields ─────────────────
    if (!editMode) {
      const inst = useAppStore.getState().instruments.find(i => i.id === config.instrumentId);
      const symbol = inst?.symbol || selectedSeries?.label || '???';
      const fieldLabel = currentField.charAt(0).toUpperCase() + currentField.slice(1);
      return (
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <InstrumentPill>{symbol}</InstrumentPill>
          <ValuePill label="Period" value={period} />
          <ValuePill label="Field" value={fieldLabel} />
          {lag > 0 && <ValuePill label="Lag" value={lag} />}
          {selectedSeries?.type === 'indicator' && <FromIndicatorBadge />}
        </div>
      );
    }

    // ── Edit mode ───────────────────────────────────
    return (
      <div className="flex flex-wrap gap-4 items-center">
        {/* Series selector – hidden if only one option */}
        {!hasSingleOption && (
          <label>
            Series:
            <select
              value={config.instrumentId}
              onChange={handleSeriesChange}
              className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
            >
              {allSeriesOptions.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label} {opt.type === 'indicator' ? '↔' : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Field selector – dynamic */}
        {availableFields.length > 1 && (
          <label>
            Field:
            <select
              value={currentField}
              onChange={e => onChange({ ...config, field: e.target.value })}
              className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
            >
              {availableFields.map(f => (
                <option key={f} value={f}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Period & Lag */}
        <label>
          Period:
          <input
            type="number"
            min="1"
            value={period}
            onChange={handlePeriodChange}
            className="ml-2 w-20 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
          />
        </label>

        <label>
          Lag:
          <input
            type="number"
            min="0"
            value={lag}
            onChange={handleLagChange}
            className="ml-2 w-20 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
          />
        </label>
      </div>
    );
  }
);

IndicatorEditorSMA.displayName = 'IndicatorEditorSMA';

export default IndicatorEditorSMA;